import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';

const DateRangePicker = ({ 
  onDateChange, 
  currentRange = { start: null, end: null, preset: 'today' },
  isMobile = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(currentRange?.preset || 'today');
  const dropdownRef = useRef(null);

  const presets = [
    { 
      key: 'today', 
      label: 'Hoje', 
      getValue: () => {
        const today = new Date();
        return { 
          start: today, 
          end: today,
          preset: 'today'
        };
      }
    },
    { 
      key: 'week', 
      label: 'Esta Semana', 
      getValue: () => {
        const today = new Date();
        const startOfWeek = new Date(today);
        startOfWeek?.setDate(today?.getDate() - today?.getDay());
        return { 
          start: startOfWeek, 
          end: today,
          preset: 'week'
        };
      }
    },
    { 
      key: 'month', 
      label: 'Este Mês', 
      getValue: () => {
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        return { 
          start: startOfMonth, 
          end: today,
          preset: 'month'
        };
      }
    },
    { 
      key: 'quarter', 
      label: 'Este Trimestre', 
      getValue: () => {
        const today = new Date();
        const quarter = Math.floor(today?.getMonth() / 3);
        const startOfQuarter = new Date(today.getFullYear(), quarter * 3, 1);
        return { 
          start: startOfQuarter, 
          end: today,
          preset: 'quarter'
        };
      }
    },
    { 
      key: 'year', 
      label: 'Este Ano', 
      getValue: () => {
        const today = new Date();
        const startOfYear = new Date(today.getFullYear(), 0, 1);
        return { 
          start: startOfYear, 
          end: today,
          preset: 'year'
        };
      }
    }
  ];

  const formatDateRange = (range) => {
    if (!range?.start) return 'Selecionar período';
    
    const preset = presets?.find(p => p?.key === range?.preset);
    if (preset) return preset?.label;
    
    const formatDate = (date) => {
      return date?.toLocaleDateString('pt-BR', { 
        day: '2-digit', 
        month: '2-digit' 
      });
    };
    
    if (range?.start?.toDateString() === range?.end?.toDateString()) {
      return formatDate(range?.start);
    }
    
    return `${formatDate(range?.start)} - ${formatDate(range?.end)}`;
  };

  const handlePresetSelect = (preset) => {
    const range = preset?.getValue();
    setSelectedPreset(preset?.key);
    onDateChange?.(range);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize with default range
  useEffect(() => {
    if (!currentRange?.start && selectedPreset) {
      const preset = presets?.find(p => p?.key === selectedPreset);
      if (preset) {
        const range = preset?.getValue();
        onDateChange?.(range);
      }
    }
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className={`
          flex items-center space-x-2 px-3 py-2 rounded-lg border border-border
          bg-card text-card-foreground hover:bg-muted transition-smooth
          font-body-normal text-sm min-h-touch
          ${isMobile ? 'w-full justify-between' : ''}
        `}
        aria-label="Selecionar período de dados"
      >
        <Icon name="Calendar" size={16} />
        <span className="whitespace-nowrap">
          {formatDateRange(currentRange)}
        </span>
        <Icon 
          name="ChevronDown" 
          size={16} 
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className={`
          absolute top-full mt-2 bg-popover border border-border rounded-lg shadow-modal
          z-dropdown min-w-48 animate-fade-in
          ${isMobile ? 'left-0 right-0' : 'right-0'}
        `}>
          <div className="p-2">
            <div className="text-xs font-body-medium text-muted-foreground px-3 py-2 uppercase tracking-wide">
              Períodos Rápidos
            </div>
            
            {presets?.map((preset) => (
              <button
                key={preset?.key}
                onClick={() => handlePresetSelect(preset)}
                className={`
                  w-full flex items-center justify-between px-3 py-2 rounded-md
                  text-sm font-body-normal transition-smooth hover:bg-accent
                  ${selectedPreset === preset?.key 
                    ? 'bg-accent text-accent-foreground' 
                    : 'text-popover-foreground'
                  }
                `}
              >
                <span>{preset?.label}</span>
                {selectedPreset === preset?.key && (
                  <Icon name="Check" size={16} className="text-primary" />
                )}
              </button>
            ))}
            
            <div className="border-t border-border my-2" />
            
            <button
              onClick={() => setIsOpen(false)}
              className="w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-body-normal text-muted-foreground hover:bg-accent transition-smooth"
            >
              <Icon name="Settings" size={16} />
              <span>Período Personalizado</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;